import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, staticUrl } from "@/lib/api";
import { Loader2, FileText, AlertTriangle } from "lucide-react";

export default function Species() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/species").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    }, []);

    return (
        <div className="ok-container py-10" data-testid="species-page">
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)]">Section 06 · Species Archive</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">Species & Entities</h1>
            <p className="opacity-70 max-w-2xl mt-2 text-sm sm:text-base leading-relaxed">
                Catalogued non-human intelligences reported in encounter records across decades and cultures.
                Each dossier aggregates physical description, classification, intelligence assessment and witness correlations.
            </p>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="species-grid">
                    {items.map((s) => <SpeciesCard key={s.slug} s={s} />)}
                </div>
            )}
        </div>
    );
}

function SpeciesCard({ s }) {
    const imgSrc = staticUrl(s.image_url);
    return (
        <Link to={`/species/${s.slug}`} className="ok-card ok-card-hover overflow-hidden group flex flex-col" data-testid={`species-card-${s.slug}`}>
            <div className="aspect-square overflow-hidden relative" style={{ background: "var(--ok-image-frame)" }}>
                {imgSrc ? (
                    <img src={imgSrc} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                        <FileText className="w-10 h-10 text-[var(--ok-accent)] opacity-30" />
                        <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-50">Dossier image pending</div>
                    </div>
                )}
                {/* archival texture */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='ns'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.65  0 0 0 0 0.78  0 0 0 0.22 0'/></filter><rect width='100%25' height='100%25' filter='url(%23ns)'/></svg>\")", opacity: 0.35, mixBlendMode: "overlay" }} />
                {/* designation chip */}
                {s.designation && (
                    <div className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5" style={{ background: "var(--ok-chip-overlay-bg)", color: "var(--ok-chip-overlay-color)", border: "1px solid var(--ok-chip-overlay-border)" }}>
                        {s.designation}
                    </div>
                )}
            </div>
            <div className="p-4 flex flex-col flex-1">
                <div className="font-display font-bold text-base group-hover:text-[var(--ok-accent)] transition-colors">{s.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 mt-1 line-clamp-1">{s.classification || s.height || ""}</div>
                <p className="text-xs opacity-70 mt-2 line-clamp-3">{s.summary}</p>
            </div>
        </Link>
    );
}
